import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ServiceBoxes from "../components/ServiceBoxes";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import SakuraPetalsCanvas from "../components/SakuraPetalsCanvas";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
  JapaneseVerticalRibbon,
} from "../components/SakuraDecorations";

export default function HomePage() {
  const faqs = [
    {
      question: "Gør akupunktur ondt?",
      answer: (
        <>
          Nej, akupunktur hos Kita Akupunktur er kendetegnet ved at være meget nænsom. Jeg anvender særligt tynde nåle (helt ned til 0,12 mm i{" "}
          <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline decoration-[#ffe0cf] hover:text-[#b3360b]">
            japansk akupunktur
          </Link>
          ), og behandlingen tilpasses altid din smertetærskel og sensitivitet.
        </>
      ),
    },
    {
      question: "Hvor mange behandlinger skal jeg have?",
      answer:
        "Antallet af behandlinger afhænger af, om der er tale om akutte eller mere kroniske symptomer. Mange mærker en positiv forandring efter 3-5 behandlinger. Ved første konsultation lægger vi en realistisk plan for dit forløb.",
    },
    {
      question: "Kan jeg få tilskud fra Sygeforsikring “danmark”?",
      answer: (
        <>
          Ja, som{" "}
          <Link to="/om-mig" className="text-[#d74814] font-medium underline decoration-[#ffe0cf] hover:text-[#b3360b]">
            RAB-registreret akupunktør
          </Link>{" "}
          opfylder jeg kravene for, at du kan få tilskud til akupunkturbehandling fra Sygeforsikring “danmark”, forudsat at du er medlem og har en lægelig stillet diagnose. Læs mere under{" "}
          <Link to="/priser" className="text-[#d74814] font-medium underline decoration-[#ffe0cf] hover:text-[#b3360b]">
            priser og tilskud
          </Link>
          .
        </>
      ),
    },
    {
      question: "Hvad er forskellen på klassisk og japansk akupunktur?",
      answer: (
        <>
          <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline decoration-[#ffe0cf] hover:text-[#b3360b]">
            Japansk akupunktur (Keiraku Chiryo)
          </Link>{" "}
          arbejder med endnu tyndere nåle, overfladisk stikteknik og et stort fokus på puls- og mavediagnose (Hara). Det er en yderst skånsom behandlingsform, der egner sig særligt godt til sensitive kroppe, børn og nåleængstelige personer.
        </>
      ),
    },
    {
      question: "Hvor ligger klinikken, og er der parkering?",
      answer: (
        <>
          Klinikken er beliggende på Kantorparken 11 i 8240 Risskov, tæt på Aarhus N, Egå og Lystrup. Der er gratis og ubesværet parkering lige ved døren. Du finder rutevejledning og åbningstider på vores{" "}
          <Link to="/kontakt" className="text-[#d74814] font-medium underline decoration-[#ffe0cf] hover:text-[#b3360b]">
            kontaktside
          </Link>
          .
        </>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Akupunktør i Risskov | Kita Akupunktur</title>
        <meta
          name="description"
          content="Nænsom japansk akupunktur hos Kita Akupunktur i Risskov nær Aarhus. Professionel behandling mod smerter, spændinger og stress. Bestil tid online."
        />
        <meta property="og:title" content="Akupunktør i Risskov | Kita Akupunktur" />
        <meta
          property="og:description"
          content="Nænsom japansk akupunktur hos Kita Akupunktur i Risskov nær Aarhus. Professionel behandling mod smerter, spændinger og stress. Bestil tid online."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/" />
        {/* LocalBusiness Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalClinic",
            "name": "Kita Akupunktur",
            "url": "https://kita-akupunktur.dk/",
            "logo": "https://assets.auxodev.dk/assets/71339d65-a05d-4c02-a540-2260acdfc245/kita-akupunktur-logo.png",
            "image": "https://assets.auxodev.dk/assets/71339d65-a05d-4c02-a540-2260acdfc245/moderne-akupunktur-klinik.jpg",
            "telephone": "+4529387186",
            "email": "kontakt@kita-akupunktur.dk",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Kantorparken 11",
              "addressLocality": "Risskov",
              "postalCode": "8240",
              "addressCountry": "DK",
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 56.196,
              "longitude": 10.231,
            },
            "priceRange": "400 - 850 DKK",
            "medicalSpecialty": "Acupuncture",
            "availableService": [
              "Akupunktur",
              "Japansk akupunktur",
              "Øre akupunktur",
              "Rygestop",
              "Vægtregulering",
            ],
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] pt-20 pb-32 lg:pt-28 lg:pb-40 overflow-hidden border-b border-[#ffe0cf]/80">
        {/* Falling Cherry Blossom Petals directly inside Hero Section */}
        <SakuraPetalsCanvas
          count={22}
          speed={0.48}
          className="absolute inset-0 w-full h-full pointer-events-none z-10 opacity-90"
        />

        {/* Botanical Cherry Branch PNG Accent in Top Right Corner */}
        <div className="absolute -top-6 -right-6 lg:top-0 lg:right-0 w-48 sm:w-64 lg:w-80 pointer-events-none z-10 opacity-75">
          <CherryBranchImg index={1} flip={true} className="w-full h-auto" />
        </div>

        {/* Subtle Zen Background Circles */}
        <div className="absolute top-1/2 -right-24 -translate-y-1/2 w-[450px] h-[450px] rounded-full border-[28px] border-[#593427]/[0.03] pointer-events-none" />
        <div className="absolute top-10 left-10 w-60 h-60 rounded-full border border-[#d74814]/10 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-5 flex flex-col justify-center">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427] font-normal leading-tight">
                Japansk akupunktur – Nænsom nålebehandling & balance i kroppen
              </h1>

              <p className="text-base sm:text-lg text-[#7a5b4f] leading-relaxed max-w-2xl">
                Individuelt tilpasset akupunkturforløb med fokus på tryghed, ro og kroppens helhed hos RAB-registreret akupunktør Mayumi Kita i Risskov.
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-3.5">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-[#d74814] text-white font-medium text-sm hover:bg-[#b83a0d] transition-all shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#d74814] focus:ring-offset-2 group"
                >
                  <SakuraFlower className="w-4 h-4 mr-2 transition-transform group-hover:rotate-90" />
                  <span>Kontakt os i dag</span>
                </Link>

                <Link
                  to="/om-mig"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-full glass-pill text-[#593427] font-medium text-sm hover:bg-white hover:text-[#d74814] transition-all focus:outline-none focus:ring-2 focus:ring-[#d74814] group"
                >
                  <span>Mød Mayumi Kita</span>
                  <i className="fa-solid fa-arrow-right ml-2 text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Link>
              </div>

              {/* Glass Badges Bottom Row */}
              <div className="pt-2 flex flex-wrap items-center gap-2 text-xs text-[#593427]">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full glass-pill">
                  <i className="fa-solid fa-check text-[#d74814] mr-1.5" aria-hidden="true" />
                  0,12 mm ultra-tynde nåle
                </span>
                <Link
                  to="/priser"
                  className="inline-flex items-center px-3 py-1.5 rounded-full glass-pill hover:bg-white hover:text-[#d74814] transition-all"
                >
                  <i className="fa-solid fa-check text-[#d74814] mr-1.5" aria-hidden="true" />
                  Tilskud fra Sygeforsikring “danmark”
                </Link>
                <span className="inline-flex items-center px-3 py-1.5 rounded-full glass-pill">
                  <i className="fa-solid fa-check text-[#d74814] mr-1.5" aria-hidden="true" />
                  Gratis parkering ved døren
                </span>
              </div>
            </div>

            {/* Right Hero Image Card */}
            <div className="lg:col-span-5 relative h-full flex flex-col justify-center">
              <div className="relative w-full h-full min-h-[340px] flex flex-col">
                <div className="absolute -top-8 -left-8 w-40 opacity-40 pointer-events-none z-0">
                  <CherryBranchImg index={2} className="w-full h-auto" />
                </div>

                <div className="w-full h-full min-h-[340px] rounded-3xl overflow-hidden shadow-xl border-4 border-white/95 bg-[#fffaf7] relative group z-10">
                  <img
                    src="https://assets.auxodev.dk/assets/71339d65-a05d-4c02-a540-2260acdfc245/akupunkturbehandling-ryg-og-laend.jpeg"
                    alt="Nænsom akupunkturbehandling af ryg og lænd hos Kita Akupunktur i Risskov"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    width="600"
                    height="450"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#3D2118]/30 via-transparent to-transparent pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Boxes directly below hero */}
      <ServiceBoxes />

      {/* Main Content Container with Optimized Spacing */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 space-y-16 lg:space-y-20 relative">
        {/* Falling Cherry Blossom Animation positioned directly behind container cards */}
        <SakuraPetalsCanvas
          count={24}
          speed={0.42}
          className="absolute inset-0 w-full h-full pointer-events-none -z-10 opacity-75"
        />
        
        {/* Section 1: Nænsom tilgang & 5 fordele (Consolidated, high-impact) */}
        <section className="glass-card rounded-3xl p-6 sm:p-10 lg:p-12 border border-[#ffe0cf] shadow-[0_16px_40px_rgba(89,52,39,0.06)] relative overflow-hidden">
          <div className="absolute inset-0 bg-shoji-pattern opacity-30 pointer-events-none" />
          <div className="absolute -top-10 -right-10 w-52 opacity-15 pointer-events-none">
            <CherryBranchImg index={4} className="w-full h-auto" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center relative z-10">
            <div className="lg:col-span-6 space-y-5">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span className="text-xs uppercase tracking-wider font-semibold text-[#d74814]">
                  Skånsom & Målrettet Tilgang
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#593427]">
                Akupunktør med nænsom nålebehandling
              </h2>

              <p className="text-[#7a5b4f] text-sm sm:text-base leading-relaxed">
                Leder du efter en akupunktør, der arbejder blidt med kroppens signaler? Hos{" "}
                <Link to="/om-mig" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b83a0d]">
                  Kita Akupunktur
                </Link>{" "}
                i Risskov nær Lystrup, Egå og Aarhus anvender jeg meget tynde nåle og{" "}
                <Link to="/japansk-akupunktur" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf]">
                  japansk akupunktur (Keiraku Chiryo)
                </Link>
                . Behandlingen føles yderst nænsom – ideel for dig med sensitiv krop eller nåleutryghed.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3 pt-1">
                <JapaneseBadge
                  label="PULSDIAGNOSE"
                  meaning="Aflæsning af meridianer og ubalancer"
                />
                <JapaneseBadge
                  label="MAVEDIAGNOSE (HARA)"
                  meaning="Nænsom berøring af kroppens centrum"
                />
              </div>
            </div>

            {/* 5 Advantages as a simple list without boxes */}
            <div className="lg:col-span-6 space-y-4 divide-y divide-[#ffe0cf]/70">
              {[
                {
                  num: "01",
                  title: "Meget tynde nåle",
                  desc: "0,12 mm tynde nåle gør indstikket næsten umærkeligt.",
                },
                {
                  num: "02",
                  title: "Overfladisk placering",
                  desc: "Blid stimulering i hudens overflade uden dybe stik.",
                },
                {
                  num: "03",
                  title: "Puls- & mavediagnose",
                  desc: "Helhedsorienteret vurdering tilpasset din krop.",
                },
                {
                  num: "04",
                  title: "Roligt klinikmiljø",
                  desc: "Fredfyldte rammer, hvor nervesystemet falder til ro.",
                },
                {
                  num: "05",
                  title: "Individuelt tilpasset",
                  desc: "Løbende justering efter din krops aktuelle respons.",
                },
              ].map((item, idx) => (
                <div key={idx} className="pt-3.5 first:pt-0 flex items-start space-x-3.5">
                  <span className="font-serif text-sm font-bold text-[#d74814] shrink-0 mt-0.5 w-6">
                    {item.num}
                  </span>
                  <div>
                    <h3 className="text-sm sm:text-base font-serif font-semibold text-[#593427]">
                      {item.title}
                    </h3>
                    <p className="text-[#7a5b4f] text-xs sm:text-sm leading-relaxed mt-0.5">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 2: Sådan arbejder jeg (Sleek 4-Column Timeline Grid) */}
        <section className="relative">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 border border-[#ffe0cf]">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span className="text-xs uppercase tracking-wider font-semibold text-[#d74814]">
                Behandlingsforløb
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Sådan arbejder jeg som akupunktør
            </h2>
            <p className="text-[#7a5b4f] text-xs sm:text-sm mt-2">
              Et trygt forløb i fire overskuelige trin tilpasset netop din krops behov.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                step: "1",
                title: "Samtale & Symptomer",
                desc: (
                  <>
                    Vi afdækker dine gener, fx{" "}
                    <Link to="/led-og-muskelsmerter" className="text-[#593427] font-medium hover:text-[#d74814] underline decoration-[#ffe0cf]">
                      smerter
                    </Link>
                    ,{" "}
                    <Link to="/stress" className="text-[#593427] font-medium hover:text-[#d74814] underline decoration-[#ffe0cf]">
                      stress
                    </Link>
                    , søvn,{" "}
                    <Link to="/spaendinger" className="text-[#593427] font-medium hover:text-[#d74814] underline decoration-[#ffe0cf]">
                      spændinger
                    </Link>{" "}
                    eller fordøjelse.
                  </>
                ),
              },
              {
                step: "2",
                title: "Puls- & Mavediagnose",
                desc: (
                  <>
                    <Link to="/japansk-akupunktur" className="text-[#593427] font-medium hover:text-[#d74814] underline decoration-[#ffe0cf]">
                      Japanske diagnosemetoder
                    </Link>{" "}
                    hjælper med at kortlægge ubalancer nænsomt.
                  </>
                ),
              },
              {
                step: "3",
                title: "Blid Nålebehandling",
                desc: "Præcis og behagelig akupunktur med ultra-tynde nåle uden ubehag.",
              },
              {
                step: "4",
                title: "Løbende Justering",
                desc: "Forløbet tilpasses fra gang til gang i takt med, at kroppen finder ro og balance.",
              },
            ].map((s) => (
              <div
                key={s.step}
                className="glass-card p-5 rounded-2xl border border-[#ffe0cf] shadow-xs hover:bg-white transition-all flex flex-col justify-between group"
              >
                <div>
                  <div className="w-9 h-9 rounded-xl bg-[#ffe0cf]/60 text-[#d74814] group-hover:bg-[#d74814] group-hover:text-white transition-colors flex items-center justify-center font-serif text-sm font-bold mb-3 border border-[#ffe0cf]">
                    {s.step}
                  </div>
                  <h3 className="text-sm font-serif font-semibold text-[#593427] mb-1.5">
                    {s.title}
                  </h3>
                  <p className="text-[#7a5b4f] text-xs leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Section 3: Restyled according to Design reference "Services" (Full bleed dark with continuous border grid) */}
      <section className="w-full relative text-white py-20 sm:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden text-center">
        {/* Background image with dark overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/71339d65-a05d-4c02-a540-2260acdfc245/japansk-inspireret-klinik-podie-blomster.jpg"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#24130C]/85 backdrop-blur-[2px]" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10 space-y-12">
          {/* Top Introductory Text Block (Centered) */}
          <div className="max-w-3xl mx-auto space-y-3 text-center">
            <span className="text-xs uppercase tracking-widest text-[#ffe0cf] font-semibold block">
              INDIKATIONER & BEHANDLING
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-white font-normal leading-tight">
              Hvilke gener kan en akupunktør hjælpe med?
            </h2>
            <p className="text-sm sm:text-base text-[#ffe0cf]/85 leading-relaxed max-w-2xl mx-auto">
              Akupunktur kan anvendes alene eller som effektivt supplement ved en bred vifte af fysiske og mentale ubalancer i hverdagen.
            </p>
          </div>

          {/* Bottom Service Grid: Continuous thin borders without gaps, sharp corners, uppercase titles, no icons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/20">
            {[
              {
                title: "HOVEDPINE & MIGRÆNE",
                desc: "Lindring af migræneanfald, spændingshovedpine og trykken for tindingerne.",
                link: "/migraene",
              },
              {
                title: "ONDT I KÆBEN",
                desc: "Spændte tyggemuskler, tænderskæren, kliklyde og låsninger i kæbeleddet.",
                link: "/ondt-i-kaeben",
              },
              {
                title: "SPÆNDINGER I NAKKE & SKULDRE",
                desc: "Myoser, infiltrationer og vedvarende stivhed i nakke, skuldre og ryg.",
                link: "/spaendinger",
              },
              {
                title: "ONDT I RYGGEN & LÆND",
                desc: "Akutte hold i ryggen, lændesmerter, iskias og stivhed i rygsøjlen.",
                link: "/ondt-i-ryggen",
              },
              {
                title: "LED- & MUSKELSMERTER",
                desc: "Smerter i knæ, hofter, frossen skulder, slidgigt og tennisalbue.",
                link: "/led-og-muskelsmerter",
              },
              {
                title: "STRESS & SØVNLØSHED",
                desc: "Indre uro, tankemylder, søvnbesvær og overbelastet nervesystem.",
                link: "/stress",
              },
              {
                title: "FORDØJELSESPROBLEMER",
                desc: "Oppustethed, mavekramper, IBS, forstoppelse og ubalancer i fordøjelsen.",
                link: "/japansk-akupunktur",
              },
              {
                title: "ALLERGI & BIHULER",
                desc: "Høfeber, bihulebetændelse, tæthed og overfølsomhed i slimhinderne.",
                link: "/akupunktur",
              },
              {
                title: "RYGESTOP BEHANDLING",
                desc: "Effektiv dæmpning af rygetrang, rastløshed og abstinenser.",
                link: "/rygestop-behandling",
              },
              {
                title: "VÆGTREGULERING",
                desc: "Naturlig støtte til sukkertrang, stofskifte og mæthedsfornemmelse.",
                link: "/vaegtregulering",
              },
              {
                title: "HORMONEL BALANCE",
                desc: "Overgangsalder, hedeture, søvnforstyrrelser og menstruationsgener.",
                link: "/oere-akupunktur",
              },
              {
                title: "ØREAKUPUNKTUR",
                desc: "Målrettet zonerelateret stimulering af ørets reflekszoner mod akutte gener.",
                link: "/oere-akupunktur",
              },
            ].map((item, idx) => (
              <Link
                key={idx}
                to={item.link}
                className="border-r border-b border-white/20 p-6 sm:p-8 flex flex-col items-center justify-center text-center rounded-none group hover:bg-white/[0.08] transition-colors focus:outline-none focus:bg-white/[0.1] block text-inherit"
              >
                <h3 className="text-xs sm:text-sm font-semibold uppercase tracking-widest text-white mb-2 group-hover:text-[#ffe0cf] transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs text-[#ffe0cf]/75 leading-relaxed mb-4">
                  {item.desc}
                </p>
                <span
                  className="mt-auto inline-flex items-center text-xs font-semibold text-[#ffe0cf] group-hover:text-white uppercase tracking-wider transition-colors"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right ml-1.5 text-[10px] transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content Container Part 2 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 space-y-16 lg:space-y-20 relative">
        {/* Section 4: FAQ */}
        <section className="pt-4">
          <FaqSection
            title="Ofte stillede spørgsmål om akupunktur"
            subtitle="Få svar på de mest almindelige spørgsmål om nænsom behandling hos Kita Akupunktur i Risskov."
            faqs={faqs}
          />
          <div className="mt-8 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center px-6 py-3 rounded-full bg-white text-[#593427] border border-[#ffe0cf] hover:border-[#d74814] text-sm font-semibold hover:bg-[#ffe0cf]/30 transition-all shadow-xs group"
            >
              <SakuraFlower className="w-4 h-4 mr-2 text-[#d74814]" />
              <span>Har du flere spørgsmål? Se vores store FAQ</span>
              <i className="fa-solid fa-arrow-right ml-2 text-xs text-[#d74814] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
